import React from 'react';
import Fetch from '../../../utils/fetch';
import REQ, { ReqEnum } from '../../../const/requestStatus';
import { settings } from '../../../settings';
import Button from '../../../components/buttons/Button';
import './OrderChat.scss';
import { Delivery, Order, OrderStatus } from '../../../types/orderTypes';
import {
  PusherEvent,
  subscribeToChannel,
  unsubscribeFromChannel,
} from '../../../utils/pusher';
import { capitalize } from '../../../utils/strings';
import dayjs from 'dayjs';

interface ChatMessageEvent {
  authorName: string;
  authorRole: string;
  messageType: 'CHAT' | 'SMS';
  created: string;
  orderId: string;
  text: string;
}

function removeDuplicateMessages(messages: ChatMessageEvent[]) {
  const uniqueValues = new Map<string, ChatMessageEvent>(
    messages.map(message => {
      const result: [string, ChatMessageEvent] = [
        new Date(message.created).getTime().toString() +
          message.text.length.toString(),
        message,
      ];
      return result;
    })
  ).values();
  return Array.from(uniqueValues);
}

function uniqueAndSortedMessages(
  messages: ChatMessageEvent[]
): ChatMessageEvent[] {
  const uniqueMessages = removeDuplicateMessages(messages);
  return uniqueMessages
    .slice()
    .sort(
      (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
    );
}

interface Props {
  order: Order;
  delivery: Delivery;
}

interface State {
  messageInput: string;
  messages: ChatMessageEvent[];
  messagesStatus: ReqEnum;
  page: number;
  sendMessageAsSms: boolean;
}

class OrderChat extends React.Component<Props, State> {
  private messagesContainer: HTMLDivElement;

  constructor(props) {
    super(props);

    this.messagesContainer = null;

    this.state = {
      messageInput: '',
      messages: [],
      messagesStatus: ReqEnum.INIT,
      page: 1,
      sendMessageAsSms: false,
    };
  }

  private chatChannelName: string = null;

  componentDidMount() {
    this.subscribeToPusherMessages();
    this.initialFetchMessages();
  }

  componentWillUnmount() {
    if (this.chatChannelName) {
      unsubscribeFromChannel(this.chatChannelName);
    }
  }

  async subscribeToPusherMessages() {
    const { token } = this.props.order;
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/order/token/${token}/channel-names`,
    });

    if (response.ok) {
      const channelNames = await response.json();
      this.chatChannelName = channelNames.deliveryChatChannel;
      subscribeToChannel(
        this.chatChannelName,
        PusherEvent.CHAT_MESSAGE,
        this.onPusherMessage.bind(this)
      );
    }
  }

  onPusherMessage(message: ChatMessageEvent) {
    this.setState(
      prevState => ({
        messages: [...prevState.messages, message],
      }),
      this.scrollToEnd
    );
  }

  initialFetchMessages = async () => {
    await this.fetchMessages();
    this.scrollToEnd();
  };

  fetchMessages = async () => {
    this.setState({ messagesStatus: ReqEnum.PENDING });

    const { messages, page } = this.state;

    const chatroomToken = this.props.delivery.chatroomToken;
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/chat/${chatroomToken}/messages?page=${page}`,
    });

    if (response.ok) {
      const newMessages: ChatMessageEvent[] = await response.json();

      this.setState({
        messages: [...messages, ...newMessages],
        messagesStatus: ReqEnum.SUCCESS,
      });

      return true;
    }

    this.setState({ messagesStatus: ReqEnum.ERROR });
    return false;
  };

  fetchOlderMessages = () => {
    this.setState({ page: this.state.page + 1 }, this.fetchMessages);
  };

  sendMessage = async (): Promise<void> => {
    const { messageInput } = this.state;

    if (messageInput === '') {
      return;
    }

    if (this.state.sendMessageAsSms) {
      this.sendMessageAsSms(messageInput);
    } else {
      const chatroomToken = this.props.delivery.chatroomToken;
      const response = await Fetch.post({
        url: `${settings.newApiUrl}/chat/${chatroomToken}/messages`,

        body: {
          message: {
            body: messageInput,
          },
        },
      });

      if (response.ok) {
        this.setState({ messageInput: '' });
      } else {
        window.alert('Could not send message');
      }
    }
  };

  sendMessageAsSms = async (message: string): Promise<void> => {
    const { id } = this.props.order;
    const response = await Fetch.post({
      url: `${settings.newApiUrl}/sms/${id}`,
      body: {
        message: {
          body: message,
        },
      },
    });

    if (response.ok) {
      this.setState({ messageInput: '', sendMessageAsSms: false });
      await this.fetchMessages();
      this.scrollToEnd();
    } else {
      window.alert('Could not send SMS');
    }
  };

  scrollToEnd = () => {
    if (this.messagesContainer) {
      this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    }
  };

  hasDefinedRole = (role: string) => {
    return role != null && role !== 'UNAUTHENTICATED';
  };

  render() {
    const { messagesStatus, messages, messageInput, sendMessageAsSms } =
      this.state;
    const isLoading = [REQ.INIT, REQ.PENDING].includes(messagesStatus);
    const sortedMessages = uniqueAndSortedMessages(messages);
    const canSendChatMessages = ![
      OrderStatus.Canceled,
      OrderStatus.Delivered,
    ].includes(this.props.order.status);

    return (
      <div className="order-chat">
        <p className="order-chat-header">
          Open chat/messages with sender, recipient, courier and Porterbuddy
          Support
        </p>

        <div
          className="order-chat-messages-container"
          ref={node => {
            this.messagesContainer = node;
          }}
        >
          <div className="order-chat-loader-container">
            <Button
              onClick={this.fetchOlderMessages}
              title="Load older messages"
              loading={isLoading}
            />
          </div>

          {sortedMessages.map(message => (
            <div
              key={message.created + String(message.text.length)}
              className="order-chat-message"
              style={{
                justifyContent:
                  message.authorRole === 'ADMIN' ? 'flex-end' : 'flex-start',
              }}
            >
              <div>
                <div className="order-chat-message-from">
                  {this.hasDefinedRole(message.authorRole)
                    ? capitalize(message.authorRole)
                    : ''}{' '}
                  {message.messageType === 'SMS' ? (
                    <span style={{ fontWeight: 'bold' }}>(SMS) </span>
                  ) : null}
                  {dayjs(message.created).format('DD.MM HH:mm')}
                </div>
                <div
                  className={`order-chat-message-content-${
                    message.authorRole === 'ADMIN' ? 'me' : 'other'
                  }`}
                >
                  {message.text}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="order-chat-input-container">
          <input
            type="text"
            value={messageInput}
            disabled={!canSendChatMessages}
            onChange={event =>
              this.setState({ messageInput: event.target.value })
            }
            onKeyPress={event => event.key === 'Enter' && this.sendMessage()}
          />
          <label>
            <input
              type="checkbox"
              checked={sendMessageAsSms}
              onChange={event =>
                this.setState({ sendMessageAsSms: event.target.checked })
              }
            />{' '}
            Send as SMS
          </label>
          <Button
            onClick={this.sendMessage}
            disabled={!canSendChatMessages}
            title="Send"
          />
        </div>
      </div>
    );
  }
}

export default OrderChat;
